import type { CSSProperties } from "react";

import { Button } from "@/components/ui/button";
import { Footer, InvoiceCard } from "@/components/invoice-layout";
import { DOLFIN_ACCENT, isDarkEnoughForText, pickForeground, tintFor } from "@/lib/brand";
import { formatCurrency } from "@/lib/format";
import type { PublicInvoiceResponse } from "@/api/public";

// A real production route: the QR on a preview invoice PDF links here. No Stripe, no backend, one neutral placeholder.

const PREVIEW_AMOUNT = 124.5;

function previewInvoice(): PublicInvoiceResponse {
  const dueDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  return {
    documentNumber: "INV-0000",
    currency: "GBP",
    totalAmount: PREVIEW_AMOUNT,
    amountPaid: 0,
    amountCredited: 0,
    amountDue: PREVIEW_AMOUNT,
    issueDate: new Date().toISOString(),
    dueDate,
    status: "Issued",
    memo: null,
    customerName: "Your customer",
    organisationName: "Your Organisation",
    organisationLogoUrl: null,
    organisationPrimaryColour: DOLFIN_ACCENT,
    whatsAppOptInAvailable: false,
    whatsAppOptedIn: false,
  };
}

export function PreviewPage() {
  const invoice = previewInvoice();
  const brand = DOLFIN_ACCENT;

  const pageStyle = {
    backgroundColor: tintFor(brand),
    ["--ring" as string]: brand,
  } as CSSProperties;

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4 sm:p-6"
      style={pageStyle}
    >
      <div className="w-full max-w-md">
        <PreviewBanner />
        <InvoiceCard
          invoice={invoice}
          brand={brand}
          amountTint={isDarkEnoughForText(brand) ? brand : undefined}
        >
          <PreviewForm brand={brand} amount={invoice.amountDue} currency={invoice.currency} />
        </InvoiceCard>
        <PreviewNote />
        <Footer />
      </div>
    </div>
  );
}

function PreviewBanner() {
  return (
    <div className="mb-3 rounded-md border bg-muted/70 px-3 py-2 text-center text-xs text-muted-foreground">
      <span className="font-medium text-foreground">Preview</span>
      {" — "}no payment will be taken on this page.
    </div>
  );
}

function PreviewNote() {
  return (
    <p className="mx-auto mt-3 max-w-xs text-center text-xs text-muted-foreground">
      Your organisation's name, logo and brand colour will be replaced by the
      values in your organisation settings when a real invoice is sent.
    </p>
  );
}

function PreviewForm({
  brand,
  amount,
  currency,
}: {
  brand: string;
  amount: number;
  currency: string;
}) {
  const payLabel = `Pay ${formatCurrency(amount, currency)}`;
  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-background p-4 text-sm">
        <div className="mb-2 font-medium">Card information</div>
        <div className="flex h-10 items-center rounded bg-muted/50 px-3 text-muted-foreground">
          1234 1234 1234 1234
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="flex h-10 items-center rounded bg-muted/50 px-3 text-muted-foreground">
            MM / YY
          </div>
          <div className="flex h-10 items-center rounded bg-muted/50 px-3 text-muted-foreground">
            CVC
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Button
          type="button"
          disabled
          className="w-full"
          style={{ backgroundColor: brand, color: pickForeground(brand) }}
          aria-label={`${payLabel} — preview, not a real invoice`}
        >
          {payLabel}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          Disabled in preview — not a real invoice.
        </p>
      </div>
    </div>
  );
}
